import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShoppingBag, Upload, Link2, Trash2, Sparkles, Package } from "lucide-react";

export default function Catalog() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/catalog").then((r) => setProducts(r.data.products)).catch(() => {});
  useEffect(() => { load(); }, []);

  const importShopify = async () => {
    if (!url.trim()) return toast.error("Entrez l'URL de la boutique");
    setLoading(true);
    try {
      const { data } = await api.post("/catalog/import-shopify", { url });
      toast.success(`${data.count} produits importés depuis Shopify !`);
      setUrl(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Import échoué"); } finally { setLoading(false); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/catalog/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.count} produits importés depuis le CSV !`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Import échoué"); } finally { setLoading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (id) => { await api.delete(`/catalog/${id}`); load(); };

  const createAd = (p) => {
    localStorage.setItem("monaga_product", JSON.stringify({ name: p.title, description: p.description, image: p.image }));
    toast.success("Produit chargé dans le Studio IA");
    navigate("/studio");
  };

  return (
    <div className="space-y-6">
      <div><h1 className="font-display text-3xl font-bold flex items-center gap-2"><ShoppingBag className="text-primary" /> Catalogue produits</h1><p className="text-muted-foreground">Synchronisez votre boutique pour générer des pubs à partir de vos produits.</p></div>

      <Tabs defaultValue="shopify">
        <TabsList className="rounded-xl">
          <TabsTrigger value="shopify" className="rounded-lg" data-testid="catalog-tab-shopify"><Link2 size={15} className="mr-1.5" /> Shopify / URL</TabsTrigger>
          <TabsTrigger value="csv" className="rounded-lg" data-testid="catalog-tab-csv"><Upload size={15} className="mr-1.5" /> Fichier CSV</TabsTrigger>
        </TabsList>
        <TabsContent value="shopify" className="mt-4">
          <div className="p-5 rounded-2xl border border-border bg-card flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1 w-full"><Label>URL de la boutique Shopify</Label><Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://ma-boutique.myshopify.com" className="mt-1.5 h-11 rounded-xl" data-testid="shopify-url-input" /></div>
            <Button onClick={importShopify} disabled={loading} className="h-11 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 text-white border-0 w-full sm:w-auto" data-testid="shopify-import-btn">{loading ? "Import…" : "Synchroniser"}</Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Astuce : fonctionne avec toute boutique Shopify au catalogue public (produits, prix et images récupérés automatiquement).</p>
        </TabsContent>
        <TabsContent value="csv" className="mt-4">
          <div className="p-5 rounded-2xl border border-border bg-card">
            <input ref={fileRef} type="file" accept=".csv" onChange={importCsv} className="hidden" data-testid="csv-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={loading} variant="outline" className="h-11 rounded-xl" data-testid="csv-import-btn"><Upload size={16} className="mr-1.5" /> Choisir un fichier CSV</Button>
            <p className="text-xs text-muted-foreground mt-2">Colonnes attendues : <span className="font-mono">title, price, image, description</span>.</p>
          </div>
        </TabsContent>
      </Tabs>

      {products.length === 0 ? (
        <div className="p-16 rounded-2xl border border-dashed border-border bg-card text-center text-muted-foreground"><Package size={44} className="mx-auto mb-3 opacity-40" /><p>Aucun produit importé. Synchronisez votre boutique ci-dessus.</p></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {products.map((p) => (
            <div key={p.product_id} className="rounded-2xl border border-border bg-card overflow-hidden group" data-testid={`product-${p.product_id}`}>
              <div className="aspect-square bg-secondary overflow-hidden relative">
                {p.image ? <img src={p.image} alt={p.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Package size={32} className="text-muted-foreground/40" /></div>}
                <button onClick={() => remove(p.product_id)} className="absolute top-2 right-2 h-7 w-7 rounded-lg bg-destructive/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
              </div>
              <div className="p-3">
                <p className="font-medium text-sm line-clamp-1">{p.title}</p>
                {p.price && <p className="text-sm text-primary font-bold mt-0.5">{p.price} €</p>}
                <Button size="sm" onClick={() => createAd(p)} className="w-full mt-2 rounded-lg text-xs bg-gradient-to-r from-indigo-500 to-cyan-500 text-white border-0" data-testid={`create-ad-${p.product_id}`}><Sparkles size={13} className="mr-1" /> Créer une pub</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
